import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { CustomAutoComplete } from "components/CustomComponents";
import { themes } from "services/constants";
import * as Mui from "@mui/material";
import moment from "moment";
import dayjs from "dayjs";
import { Edit, Delete } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { leaveManagementError } from "services/constants/ErrorMessages";
import { GlobalDialogBox } from "components/GlobalDialogBox";
import { styled } from "@mui/system";
import { AddLeave } from "./AddLeave";
import {
  CustomButton,
  InfoMessage,
} from "components/CustomComponents/CustomButton";
import sessionHandling from "services/utils/notificationUtils";
import { formKeyText } from "services/constants/Styles";
import { Info } from "@mui/icons-material";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { tooltipStyleProps } from "services/constants/Styles";

const HoverIconButton = styled(Mui.IconButton)`
  &:hover {
    color: ${({ theme }) => theme.palette.info.main};
  }
`;

const HoverDeleteIconButton = styled(Mui.IconButton)`
  &:hover {
    color: ${({ theme }) => theme.palette.error.main};
  }
`;

export const Holidays = () => {
  const { domain, token, userDetails, globalSearchValue, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const [showForm, setShowForm] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [masterDataLeaves, setMasterDataLeaves] = useState([]);
  const [editLeaveId, setEditLeaveId] = useState(null);
  const [editedFromDate, setEditedFromDate] = useState("");
  const [editedLeaveType, setEditedLeaveType] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [filteredLabel, setFilteredLabel] = useState("");
  const [filteredValue, setFilteredValue] = useState("");
  const [leaveDate, setLeaveDate] = useState([]);
  const [leaveStartDate, setLeaveStartDate] = useState("");
  const [leaveEndDate, setLeaveEndDate] = useState("");
  const [getDataFromApi, setGetDataFromApi] = useState(true);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const containerRef = useRef(null);

  const {
    register,
    formState: { errors },
    reset,
  } = useForm({ mode: "onBlur" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${domain}companyleave/?start_date=${leaveStartDate}&end_date=${leaveEndDate}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setMasterDataLeaves(res);
          setLoading(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        showToast("error", error.message);
        setLoading(false);
      }
    };
    if (getDataFromApi && leaveStartDate && leaveEndDate) {
      fetchData();
      setGetDataFromApi(false);
    }
  }, [domain, getDataFromApi, leaveEndDate, leaveStartDate, token]);

  const handleLeaveAdded = (newLeave) => {
    setGetDataFromApi(true);
  };

  useEffect(() => {
    const fetchLeaveDate = async () => {
      try {
        const response = await fetch(`${domain}leaves-years/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          const leaveDate = res.map((value) => ({
            label: value.name,
            start_date: value.start_date,
            end_date: value.end_date,
            filteredValue: `${value.start_date} - ${value.end_date}`,
          }));
          const sDate = formatDate(res[0]?.start_date);
          const eDate = formatDate(res[0]?.end_date);
          setLeaveDate(leaveDate);
          setFilteredLabel(res[0]?.name);
          setFilteredValue(`${res[0]?.start_date} - ${res[0]?.end_date}`);
          setLeaveStartDate(sDate);
          setLeaveEndDate(eDate);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        showToast("error", error.message);
      }
    };
    fetchLeaveDate();
  }, [domain, token]);

  const formatDate = (dateString) => {
    const [day, month, year] = dateString.split("-");
    return `${year}-${month}-${day}`;
  };

  const handleSaveEdit = async () => {
    const updatedLeaves = leaves.map((leave) => {
      if (leave.id === editLeaveId) {
        return {
          ...leave,
          date: moment(editedFromDate, "YYYY-MM-DD").format("DD-MM-YYYY"),
          leave_type: editedLeaveType,
        };
      }
      return leave;
    });
    if (editedFromDate !== "" && editedLeaveType !== "") {
      try {
        const response = await fetch(`${domain}companyleave/${editLeaveId}/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify({
            date: editedFromDate,
            leave_type: editedLeaveType,
            createdby: userDetails.firstname,
            createdat: new Date().toISOString(),
            modifiedby: userDetails.firstname,
            modifiedat: new Date().toISOString(),
          }),
        });
        const res = await response.json();
        if (response.ok) {
          showToast("success", "Company holiday details updated successfully.");
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        setGetDataFromApi(true);
        showToast("error", error.message);
      }

      setLeaves(updatedLeaves);
      setEditLeaveId(null);
      setEditedFromDate("");
      setEditedLeaveType("");
      reset();
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [page]);

  const handleLeaveEdit = (leave) => {
    setEditLeaveId(leave.id);
    const formattedDate = formatDate(leave.date);
    const isoDate = new Date(formattedDate).toISOString().substring(0, 10);
    setEditedFromDate(isoDate);
    setEditedLeaveType(leave.leave_type);
  };

  const handleDeleteConfirmation = (leaveId) => {
    setDeleteConfirmation(leaveId);
  };

  const handleDeleteCancel = (event) => {
    setDeleteConfirmation(null);
  };

  const handleEditCancel = (event) => {
    setEditLeaveId(null);
    reset();
  };

  const handleDeleteLeave = async (leaveId) => {
    try {
      const response = await fetch(`${domain}companyleave/${leaveId}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          isdeleted: true,
          date: new Date().toISOString().split("T")[0],
          createdby: userDetails.firstname,
          createdat: new Date().toISOString(),
          modifiedby: userDetails.firstname,
          modifiedat: new Date().toISOString(),
        }),
      });
      const res = await response.json();
      if (response.ok) {
        setGetDataFromApi(true);
        showToast("success", leaveManagementError.deletedleaveError);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error(res.error);
      }
      setLeaves((prevLeaves) =>
        prevLeaves.filter((leave) => leave.id !== leaveId)
      );
    } catch (error) {
      showToast("error", error.message);
    } finally {
      setGetDataFromApi(true);
    }

    setDeleteConfirmation(null);
  };

  const handleDialogClick = (event) => {
    event.stopPropagation();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleKeyDown = (e) => {
    const allowedKeys = /^[A-Za-z\s]$/;
    if (!allowedKeys.test(e.key)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const searchFields = ["date", "leave_type"];
    const filteredResults = masterDataLeaves.filter((item) =>
      searchFields.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setLeaves(filteredResults);
    setPage(globalSearchValue ? 0 : page);
  }, [globalSearchValue, masterDataLeaves, page]);

  const handleShowForm = () => {
    setShowForm(true);
  };

  const updateLeavePeriod = (event, value) => {
    const today = dayjs().format("YYYY-MM-DD");
    if (value && value.filteredValue) {
      setLeaveStartDate(formatDate(value.start_date));
      setLeaveEndDate(formatDate(value.end_date));
      setFilteredLabel(value.label);
      setFilteredValue(value.filteredValue);
      setGetDataFromApi(true);
    } else {
      setFilteredLabel("");
      setFilteredValue("");
      setLeaveStartDate("");
      setLeaveEndDate("");
      setFilteredLabel("");
      setFilteredValue("");
      setLeaveStartDate(today);
      setLeaveEndDate(today);
    }
  };

  return (
    <React.Fragment>
      <AddLeave
        onLeaveAdded={handleLeaveAdded}
        showForm={showForm}
        setShowForm={setShowForm}
      />
      <Mui.Grid container sx={{ paddingTop: 5, paddingBottom: 1 }}>
        <Mui.Grid item xs={3} sx={{ padding: 1 }}>
          <CustomAutoComplete
            dataList={leaveDate}
            selectedValue={filteredValue}
            updateDataFunction={updateLeavePeriod}
            placeHolder="Select leave period"
            label={filteredLabel ? filteredLabel : "Leave Period"}
          />
        </Mui.Grid>
        <Mui.Grid item xs={9}>
          <Mui.Box
            sx={{
              position: "relative",
              textAlign: "right",
              paddingRight: 2.5,
            }}
          >
            <CustomButton
              actionFuntion={() => {
                if (
                  checkEditorPermission(
                    userScreenAccess?.module_details,
                    "LMS",
                    "Holidays"
                  )
                ) {
                  handleShowForm();
                } else {
                  showToast(
                    "error",
                    "Access denied: You are not authorized to add holiday"
                  );
                }
              }}
              actionName="Add Holiday"
              typeName="button"
            />
          </Mui.Box>
        </Mui.Grid>
      </Mui.Grid>
      {loading ? (
        <Loader info="Loading..." />
      ) : (
        <Mui.Grid container>
          <Mui.TableContainer
            ref={containerRef}
            sx={{ padding: 1, ml: 2, mr: 2 }}
          >
            <Mui.Table className="border">
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                }}
              >
                <Mui.TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      height: "10px",
                      padding: "3px 15px",
                    },
                  }}
                >
                  <Mui.TableCell>
                    <Mui.Typography fontWeight="bold">Date</Mui.Typography>
                  </Mui.TableCell>
                  <Mui.TableCell>
                    <Mui.Typography fontWeight="bold">
                      Holiday Name
                    </Mui.Typography>
                  </Mui.TableCell>

                  <Mui.TableCell>
                    <Mui.Typography fontWeight="bold">Actions</Mui.Typography>
                  </Mui.TableCell>
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {leaves.length === 0 ? (
                  <InfoMessage dataLength={masterDataLeaves} />
                ) : (
                  leaves
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((leave) => (
                      <Mui.TableRow
                        key={leave.id}
                        sx={{
                          "& .MuiTableCell-root": {
                            height: "10px",
                            padding: "3px 15px",
                          },
                        }}
                      >
                        <>
                          <Mui.TableCell> {leave.date}</Mui.TableCell>
                          <Mui.TableCell>{leave.leave_type}</Mui.TableCell>

                          <Mui.TableCell>
                            <HoverIconButton
                              onClick={() => {
                                if (
                                  checkEditorPermission(
                                    userScreenAccess?.module_details,
                                    "LMS",
                                    "Holidays"
                                  )
                                ) {
                                  handleLeaveEdit(leave);
                                } else {
                                  showToast(
                                    "error",
                                    "Access denied: You are not authorized to edit holiday"
                                  );
                                }
                              }}
                            >
                              <Edit />
                            </HoverIconButton>
                            <HoverDeleteIconButton
                              onClick={() => {
                                if (
                                  checkEditorPermission(
                                    userScreenAccess?.module_details,
                                    "LMS",
                                    "Holidays"
                                  )
                                ) {
                                  handleDeleteConfirmation(leave.id);
                                } else {
                                  showToast(
                                    "error",
                                    "Access denied: You are not authorized to delete holiday"
                                  );
                                }
                              }}
                            >
                              <Delete />
                            </HoverDeleteIconButton>
                          </Mui.TableCell>
                        </>
                      </Mui.TableRow>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
        </Mui.Grid>
      )}
      {leaves.length > 10 && (
        <Mui.TablePagination
          sx={{ marginRight: 5 }}
          className="custom-pagination"
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={leaves.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      <GlobalDialogBox
        handleCloseDialog={handleDeleteCancel}
        open={deleteConfirmation}
        title="Delete Holiday"
        onClick={handleDialogClick}
      >
        <Mui.Grid container>
          <Mui.Grid item xs={12}>
            <Mui.Typography fontSize={24}>
              Are you sure you want to delete this leave?
            </Mui.Typography>
          </Mui.Grid>
        </Mui.Grid>
        <Mui.Grid container>
          <Mui.Grid item xs={8}></Mui.Grid>
          <Mui.Grid item xs={2}>
            <CustomButton
              actionFuntion={handleDeleteCancel}
              actionName="No"
              typeName="button"
            />
          </Mui.Grid>
          <Mui.Grid item xs={2}>
            <CustomButton
              actionFuntion={() => handleDeleteLeave(deleteConfirmation)}
              actionName="Yes"
              typeName="button"
            />
          </Mui.Grid>
        </Mui.Grid>
      </GlobalDialogBox>

      <GlobalDialogBox
        handleCloseDialog={handleEditCancel}
        open={editLeaveId}
        title="Edit Holiday"
        onClick={handleDialogClick}
      >
        <Mui.Grid container>
          <Mui.Grid container>
            <Mui.Grid xs={4}>
              <Mui.Typography sx={formKeyText}>
                Date{" "}
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Update the holiday date if there is a change in its observance for the current year."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={8}>
              <Mui.TextField
                id={`from-date-${editLeaveId}`}
                type="date"
                size="small"
                value={editedFromDate}
                {...register(`fromDate-${editLeaveId}`, {
                  required: "Date is required.",
                })}
                onChange={(e) => setEditedFromDate(e.target.value)}
                fullWidth
              />
              {errors[`fromDate-${editLeaveId}`] && (
                <Mui.Typography color="error" variant="body2">
                  Date is required.
                </Mui.Typography>
              )}
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid container sx={{ paddingTop: 1 }}>
            <Mui.Grid xs={4}>
              <Mui.Typography sx={formKeyText}>
                Holiday Name{" "}
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title="Modify the name of the holiday if needed to ensure accurate identification in the leave calendar."
                >
                  <Info />
                </Mui.Tooltip>
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid xs={8}>
              <Mui.TextField
                size="small"
                placeholder="Enter holiday name"
                {...register(`holidayname-${editLeaveId}`, {
                  required: "Holiday name is required",
                  minLength: {
                    value: 3,
                    message: "Minimum length is 3 characters",
                  },
                  maxLength: {
                    value: 30,
                    message: "Maximum length is 30 characters",
                  },
                })}
                value={editedLeaveType}
                onKeyPress={handleKeyDown}
                onChange={(e) => setEditedLeaveType(e.target.value)}
                fullWidth
              />
              {errors[`holidayname-${editLeaveId}`] && (
                <Mui.Typography color="error" variant="body2">
                  {errors[`holidayname-${editLeaveId}`].message}
                </Mui.Typography>
              )}
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid container sx={{ paddingTop: 1 }}>
            <Mui.Grid item xs={8}></Mui.Grid>
            <Mui.Grid item xs={2}>
              <CustomButton
                actionFuntion={handleEditCancel}
                actionName="Cancel"
                typeName="button"
              />
            </Mui.Grid>
            <Mui.Grid item xs={2}>
              <CustomButton
                actionFuntion={() => handleSaveEdit()}
                actionName="Update"
                typeName="button"
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>
      </GlobalDialogBox>
    </React.Fragment>
  );
};
